// src/services/Api.js
import axios from "axios";
import { clearSession, isTokenExpired } from "./sessionUtils";

// ——— Resuelve baseURL (env de build, env inyectado, fallback dev/prod) ———
function resolveBaseURL() {
  // .env de CRA: REACT_APP_API_URL o REACT_APP_API_BASE
  let url =
    (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE || "").trim();

  // Variables inyectadas en runtime (por si no recompilas para cambiar URL)
  if (!url && typeof window !== "undefined") {
    const injected =
      (window.__ENV__ && window.__ENV__.API_URL) ||
      (window._env_ && window._env_.API_URL) ||
      "";
    url = (injected || "").trim();
  }

  // Fallback por entorno
  if (!url) {
    url =
      process.env.NODE_ENV === "development"
        ? "http://localhost:5081/api"
        : "/api";
  }

  // Al abrir el frontend desde otro equipo de la misma red, `localhost`
  // apuntaría a ese equipo. En desarrollo conservamos el puerto configurado,
  // pero usamos automáticamente el host con el que se abrió la aplicación.
  if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
    try {
      const parsed = new URL(url, window.location.origin);
      const hostLocal = parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1";
      const paginaRemota = window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1";
      if (hostLocal && paginaRemota) {
        parsed.hostname = window.location.hostname;
        url = parsed.toString();
      }
    } catch {
      // Si es una ruta relativa, se conserva tal como fue configurada.
    }
  }

  // Quita barras finales
  url = url.replace(/\/+$/, "");

  // Guard anti “Mixed Content”: si la app está en https, no llames a http://...
  if (typeof window !== "undefined" &&
      window.location.protocol === "https:" &&
      url.startsWith("http://")) {
    url = "/api";
  }

  return url;
}

let baseURL = resolveBaseURL();
let redirectingToLogin = false;

const api = axios.create({
  baseURL, // sin barra final
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
  timeout: 20000,
});

// Caché de navegación: conserva catálogos/listas brevemente y evita que dos
// componentes disparen la misma consulta al mismo tiempo. Nunca almacena
// telemetría, estados físicos, órdenes, sincronización ni OTA.
const getCache = new Map();
const inFlightGets = new Map();
const NEVER_CACHE = /(valores-referencia|TiposParametros|sensores\/tipos|registros|buscar|estado|telemetr|sincronizar|firmware|ota|deployments|proximo-evento|api-key|resumen)/i;
const STATIC_CACHE = /(tipos|pin-capabilities|roles|valores-referencia)/i;
const CACHEABLE = /(mis-estanques|mis-dispositivos|\/sensores\/todos|usuarios-temporales-creados|permisostemporales\/listar|\/usuarios\/perfil)/i;

const cacheKey = (url, config = {}) => {
  const token = localStorage.getItem("token") || "anon";
  const params = config.params ? JSON.stringify(config.params, Object.keys(config.params).sort()) : "";
  return `${token}|${api.defaults.baseURL}|${url}|${params}`;
};

export function clearGetCache() {
  getCache.clear();
  inFlightGets.clear();
}

function redirectToExpiredSession() {
  if (redirectingToLogin || typeof window === "undefined") return;
  redirectingToLogin = true;
  clearGetCache();
  clearSession();
  if (window.location.pathname !== "/" || !window.location.search.includes("sesion=expirada")) {
    window.location.replace("/?sesion=expirada");
  }
}

export function prefetchNavigationData() {
  return Promise.allSettled([
    api.cachedGet("/Estanques/mis-estanques"),
    api.cachedGet("/Dispositivos/mis-dispositivos"),
    api.cachedGet("/Sensores/todos"),
    api.cachedGet("/Sensores/tipos"),
    api.cachedGet("/Efectores/tipos"),
  ]);
}

api.cachedGet = async (url, config = {}) => {
  const text = String(url || "");
  if (NEVER_CACHE.test(text) || (!CACHEABLE.test(text) && !STATIC_CACHE.test(text))) {
    return api.get(url, config);
  }
  const key = cacheKey(url, config);
  const now = Date.now();
  const ttl = STATIC_CACHE.test(text) ? 6 * 60 * 60 * 1000 : 30 * 1000;
  const cached = getCache.get(key);
  if (cached && now - cached.savedAt < ttl) return cached.response;
  if (inFlightGets.has(key)) return inFlightGets.get(key);

  const pending = api.get(url, config)
    .then((response) => {
      getCache.set(key, { response, savedAt: Date.now() });
      return response;
    })
    .finally(() => inFlightGets.delete(key));
  inFlightGets.set(key, pending);
  return pending;
};

// JWT en headers (desde localStorage)
api.interceptors.request.use((config) => {
  config.headers["X-Client-Version"] = process.env.REACT_APP_VERSION || "desconocida";
  const token = localStorage.getItem("token");
  const dentroDelDashboard = window.location.pathname.startsWith("/dashboard");

  if (token && isTokenExpired(token)) {
    redirectToExpiredSession();
    return Promise.reject(new axios.CanceledError("La sesión expiró."));
  }

  // Evita que componentes que se están desmontando vuelvan a consultar la API
  // después de que otra petición ya haya cerrado la sesión.
  if (!token && dentroDelDashboard) {
    redirectToExpiredSession();
    return Promise.reject(new axios.CanceledError("Redirigiendo al inicio de sesión."));
  }

  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (config.method && !["get", "head", "options"].includes(config.method.toLowerCase())) {
    clearGetCache();
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    error.isUnauthorized = error?.response?.status === 401;
    error.isForbidden = error?.response?.status === 403;
    if (error.isUnauthorized &&
        (localStorage.getItem("token") || window.location.pathname.startsWith("/dashboard")))
      redirectToExpiredSession();
    return Promise.reject(error);
  }
);

// (Opcional) Manejo centralizado de 401/403
// api.interceptors.response.use(
//   r => r,
//   err => {
//     const status = err?.response?.status;
//     if (status === 401) {
//       // logout()/redireccionar si quieres
//     }
//     return Promise.reject(err);
//   }
// );

// Permite cambiar baseURL en caliente si alguna vez lo necesitas
export function setBaseURL(next) {
  const cleaned = String(next || "").trim().replace(/\/+$/, "");
  if (cleaned) {
    baseURL = cleaned;
    api.defaults.baseURL = cleaned;
  }
}

export { baseURL };
export default api;
